import { createAsyncThunk, createSlice, nanoid } from '@reduxjs/toolkit'
import i18next from 'i18next'
import { isEqual } from 'lodash'
import { suppliersApi } from 'src/redux/api/graphql/suppliersApi'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type { ReduxSuppliers } from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { fetchSuppliersByTenantIdUtil } from 'src/utils/fetchUtils'
import {
	aicpSubCategories,
	categories,
	cnc5chSubCategories,
	photoGridSubCategories,
	postProductionCategories,
	quote360TemplateCategories,
	quoteAnimationAndLiveActionSubCategories,
	quoteAnimationOnlySubCategories,
	quoteDigitalSubCategories,
	soundProdSubCategories,
} from 'src/utils/quoteGridUtils/createQuoteUtil'
import {
	getSupplierUuids,
	getUpdatedSupplierUuids,
} from 'src/utils/supplierUtils/getSupplierUuids'
import * as mutations from '../../graphql/mutations'
import * as queries from '../../graphql/queries'
import { makeErrorToast, makeSuccessToast, msg } from '../../utils/makeToast'

type SuppliersState = {
	suppliersData: ReduxSuppliers[]
	fetchSupplierApiStatus: ApiStatus
	updateArchiveApiStatus: ApiStatus
	createOrUpdateSupplierApiStatus: ApiStatus
	viewSupplierStatus: ApiStatus
	deleteSupplierApiStatus: ApiStatus
	importSupplierApiStatus: ApiStatus
	createSupplierModalOpen: boolean
	importSupplierModalOpen: boolean
	supplierArchivedConfirmationDialogOpenStatus: boolean
	viewSupplierOpen: boolean
	supplierDeleteDialogOpenStatus: boolean
	currentSupplier: ReduxSuppliers | null
	supplierEditModalStatus: { isOpen: boolean }
	editSupplierMenuAnchorEl: any
	importSuppliersMessageDialog: boolean
	error?: string | null
}
const initialState: SuppliersState = {
	suppliersData: [],
	fetchSupplierApiStatus: 'idle',
	updateArchiveApiStatus: 'idle',
	createOrUpdateSupplierApiStatus: 'idle',
	viewSupplierStatus: 'idle',
	deleteSupplierApiStatus: 'idle',
	importSupplierApiStatus: 'idle',
	createSupplierModalOpen: false,
	importSupplierModalOpen: false,
	supplierArchivedConfirmationDialogOpenStatus: false,
	viewSupplierOpen: false,
	supplierDeleteDialogOpenStatus: false,
	currentSupplier: null,
	supplierEditModalStatus: { isOpen: false },
	editSupplierMenuAnchorEl: null,
	importSuppliersMessageDialog: false,
	error: null,
}

// Fetches contacts that has users tenantId and the active flag is true
export const fetchSuppliers = createAsyncThunk<
	ReduxSuppliers[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>('suppliers/fetchSuppliers', async (arg, { getState, rejectWithValue }) => {
	const { auth } = getState()
	const userTableInfo = auth.userTableInfo
	if (!userTableInfo) {
		return rejectWithValue('User table info is not set')
	}
	try {
		const suppliersData = (await fetchSuppliersByTenantIdUtil(
			userTableInfo.currentCompanyTenantId
		)) as ReduxSuppliers[]
		return suppliersData
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const getSupplierDetails = createAsyncThunk<
	ReduxSuppliers,
	string,
	{
		state: RootState
		rejectValue: string
	}
>(
	'suppliers/getSupplierDetails',
	async (supplierId, { getState, rejectWithValue }) => {
		console.log('supplierId: ', supplierId)
		try {
			const {
				data: { getSuppliers: items },
			} = (await API.graphql(
				graphqlOperation(queries.getSuppliers, {
					id: supplierId,
				})
			)) as {
				data: { getSuppliers: ReduxSuppliers }
			}
			return items
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Creates a new contact item with info provided by user and then fetches contacts again
export const createSupplier = createAsyncThunk<
	void,
	any,
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'suppliers/createSupplier',
	async (supplierInfo, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tSuppliers = i18next.getFixedT(null, 'suppliers')
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			let newSupplier = {
				id: nanoid(),
				tenantId: userTableInfo.currentCompanyTenantId,
				activeFlag: true,
				...supplierInfo,
			}
			// newSupplier.internalADR = parseFloat(newSupplier.internalADR)
			// if (isNaN(newSupplier.internalADR)) {
			//   newSupplier.internalADR = null
			// }

			newSupplier = getSupplierUuids({ newSupplierData: newSupplier })

			await API.graphql(
				graphqlOperation(mutations.createSuppliers, {
					input: newSupplier,
				})
			)
			makeSuccessToast(tSuppliers('toasts.addedSupplier'))
			dispatch(fetchSuppliers())
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const importSuppliers = createAsyncThunk<
	void,
	any,
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'suppliers/importSuppliers',
	async (suppliersData, { getState, dispatch, rejectWithValue }) => {
		const tSuppliers = i18next.getFixedT(null, 'suppliers')
		console.log('supplierData: ', suppliersData)
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			if (!userTableInfo) {
				return rejectWithValue('User table info is not set')
			}
			if (suppliersData?.length > 0) {
				await Promise.all(
					suppliersData.map(async (s: any) => {
						let newSubAccount
						if (s.supplierSubAccount !== '') {
							newSubAccount = `${s.supplierSubAccount}`
						} else {
							if (s.name.length > 6) {
								newSubAccount = `${s.name.substring(0, 6)}`
							} else {
								newSubAccount = `${s.name}`.padEnd(6, '0')
							}
						}
						s.supplierSubAccount = newSubAccount

						// Default quote categories
						s.apfpCategoryUuid = categories.find(
							(i) => i.supplierDefault
						)?.categoryUuid
						s.postProdCategoryUuid = postProductionCategories.find(
							(i) => i.supplierDefault
						)?.categoryUuid
						s.aicpSubCategoryUuid = aicpSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid
						s.photoSubCategoryUuid = photoGridSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid
						s.soundProdSubCategoryUuid =
							soundProdSubCategories.find(
								(i) => i.supplierDefault
							)?.subCategoryUuid

						s.cnc5chSubCategoryUuid = cnc5chSubCategories.find(
							(i) => i.supplierDefault
						)?.subCategoryUuid

						s.quoteAnimationOnlySubCategoryUuid =
							quoteAnimationOnlySubCategories.find(
								(i) => i.supplierDefault
							)?.subCategoryUuid

						s.quoteAnimationAndLiveActionSubCategoryUuid =
							quoteAnimationAndLiveActionSubCategories.find(
								(i) => i.supplierDefault
							)?.subCategoryUuid

						s.quoteDigitalSubCategoryUuid =
							quoteDigitalSubCategories.find(
								(i) => i.supplierDefault
							)?.subCategoryUuid

						s.quote360TemplateCategoryUuid =
							quote360TemplateCategories.find(
								(i) => i.supplierDefault
							)?.categoryUuid

						let newSupplier = {
							id: nanoid(),
							tenantId: userTableInfo.currentCompanyTenantId,
							activeFlag: true,
							...s,
						}
						newSupplier = getSupplierUuids({
							newSupplierData: newSupplier,
						})
						console.log('newSupplier: ', newSupplier)
						await API.graphql(
							graphqlOperation(mutations.createSuppliers, {
								input: newSupplier,
							})
						)
					})
				)
				makeSuccessToast(tSuppliers('toasts.importedSuppliers'))
				dispatch(fetchSuppliers())
			}
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Updates the contacts & fetches the contacts again
export const updateSupplier = createAsyncThunk<
	void,
	{ supplierId: string; newSupplier: any },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'suppliers/updateSupplier',
	async ({ supplierId, newSupplier }, { dispatch, rejectWithValue }) => {
		const tSuppliers = i18next.getFixedT(null, 'suppliers')
		console.log('newSupplier: ', newSupplier)
		console.log('supplierId: ', supplierId)
		try {
			newSupplier = getUpdatedSupplierUuids({
				newSupplierData: newSupplier,
			})
			// newSupplier.internalADR = parseFloat(newSupplier.internalADR)
			// if (isNaN(newSupplier.internalADR)) {
			//   newSupplier.internalADR = null
			// }
			const {
				data: { getSuppliers },
			} = (await API.graphql(
				graphqlOperation(queries.getSuppliers, { id: supplierId })
			)) as {
				data: { getSuppliers: ReduxSuppliers }
			}
			const updateSupplierParms: Partial<ReduxSuppliers> = {}
			updateSupplierParms.id = getSuppliers.id

			for (const property in newSupplier) {
				const typedProperty = property as keyof ReduxSuppliers
				if (
					!isEqual(
						newSupplier[typedProperty],
						getSuppliers[typedProperty]
					)
				) {
					updateSupplierParms[typedProperty] =
						newSupplier[typedProperty]
				}
			}
			delete updateSupplierParms._deleted
			delete updateSupplierParms.createdOn
			delete updateSupplierParms.updatedOn
			delete updateSupplierParms._lastChangedAt
			await API.graphql(
				graphqlOperation(mutations.updateSuppliers, {
					input: updateSupplierParms,
				})
			)
			makeSuccessToast(tSuppliers('toasts.updatedSupplier'))
			dispatch(fetchSuppliers())
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Deletes the contacts from id & fetches the contacts again
export const deleteSupplier = createAsyncThunk<
	void,
	{ supplierInfo: any },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'supplier/deleteSupplier',
	async ({ supplierInfo }, { dispatch, rejectWithValue }) => {
		const tSuppliers = i18next.getFixedT(null, 'suppliers')
		console.log('supplierInfo: ', supplierInfo)
		try {
			await API.graphql(
				graphqlOperation(mutations.updateSuppliers, {
					input: {
						id: supplierInfo.id,
						activeFlag: false,
					},
				})
			)
			makeSuccessToast(tSuppliers('toasts.deletedSupplier'))
			dispatch(fetchSuppliers())
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const updateArchiveStatus = createAsyncThunk<
	void,
	{ currentSupplierId: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'suppliers/updateArchiveStatus',
	async ({ currentSupplierId }, { dispatch, rejectWithValue }) => {
		const tSuppliers = i18next.getFixedT(null, 'suppliers')
		try {
			const {
				data: { getSuppliers },
			} = (await API.graphql(
				graphqlOperation(queries.getSuppliers, {
					id: currentSupplierId,
				})
			)) as {
				data: { getSuppliers: ReduxSuppliers }
			}

			const updatedSupplier = {
				id: getSuppliers.id,
				isArchived: !getSuppliers.isArchived,
			}
			await API.graphql(
				graphqlOperation(mutations.updateSuppliers, {
					input: updatedSupplier,
				})
			)

			dispatch(fetchSuppliers())
			makeSuccessToast(
				getSuppliers.isArchived
					? tSuppliers('toasts.unarchiveSupplier')
					: tSuppliers('toasts.archiveSupplier')
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const supplierSlice = createSlice({
	name: 'suppliers',
	initialState,
	reducers: {
		setCreateSupplierModalOpen: (state, action) => {
			state.createSupplierModalOpen = action.payload.status
		},
		setImportSupplierModalOpen: (state, action) => {
			state.importSupplierModalOpen = action.payload.status
		},
		setViewSupplierOpen: (state, action) => {
			state.viewSupplierOpen = action.payload.status
			if (!action.payload.status) {
				state.currentSupplier = null
			}
		},
		setSupplierDeleteDialogOpenStatus: (state, action) => {
			state.supplierDeleteDialogOpenStatus = action.payload.status
			if (!action.payload.status) {
				state.currentSupplier = null
			}
		},
		setEditSupplierMenuAnchorEl: (state, action) => {
			state.editSupplierMenuAnchorEl = action.payload
		},
		setSupplierEditModalStatus: (state, action) => {
			state.supplierEditModalStatus = action.payload
		},
		setCurrentSupplier: (state, action) => {
			state.currentSupplier = action.payload
		},
		setSupplierArchivedConfirmationDialogOpenStatus: (state, action) => {
			state.supplierArchivedConfirmationDialogOpenStatus =
				action.payload.status
		},
		setImportSuppliersMessageDialog: (state, action) => {
			state.importSuppliersMessageDialog = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchSuppliers.pending, (state, action) => {
			state.fetchSupplierApiStatus = 'loading'
		})
		builder.addCase(fetchSuppliers.fulfilled, (state, action) => {
			state.fetchSupplierApiStatus = 'succeeded'
			state.suppliersData = action.payload
		})
		builder.addCase(fetchSuppliers.rejected, (state, action) => {
			state.fetchSupplierApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(createSupplier.pending, (state, action) => {
			state.createOrUpdateSupplierApiStatus = 'loading'
		})
		builder.addCase(createSupplier.fulfilled, (state, action) => {
			state.createOrUpdateSupplierApiStatus = 'succeeded'
			state.createSupplierModalOpen = false
		})
		builder.addCase(createSupplier.rejected, (state, action) => {
			state.createOrUpdateSupplierApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(importSuppliers.pending, (state, action) => {
			state.importSupplierApiStatus = 'loading'
		})
		builder.addCase(importSuppliers.fulfilled, (state, action) => {
			state.importSupplierApiStatus = 'succeeded'
			state.importSupplierModalOpen = false
			state.importSuppliersMessageDialog = true
		})
		builder.addCase(importSuppliers.rejected, (state, action) => {
			state.importSupplierApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(getSupplierDetails.pending, (state, action) => {
			state.viewSupplierStatus = 'loading'
		})
		builder.addCase(getSupplierDetails.fulfilled, (state, action) => {
			state.viewSupplierStatus = 'succeeded'
			state.currentSupplier = action.payload
		})
		builder.addCase(getSupplierDetails.rejected, (state, action) => {
			state.viewSupplierStatus = 'failed'
			state.currentSupplier = null
			state.error = action.error.message
		})
		builder.addCase(updateSupplier.pending, (state, action) => {
			state.createOrUpdateSupplierApiStatus = 'loading'
		})
		builder.addCase(updateSupplier.fulfilled, (state, action) => {
			state.createOrUpdateSupplierApiStatus = 'succeeded'
			state.supplierEditModalStatus.isOpen = false
			state.currentSupplier = null
		})
		builder.addCase(updateSupplier.rejected, (state, action) => {
			state.createOrUpdateSupplierApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(updateArchiveStatus.pending, (state, action) => {
			state.updateArchiveApiStatus = 'loading'
		})
		builder.addCase(updateArchiveStatus.fulfilled, (state, action) => {
			state.updateArchiveApiStatus = 'succeeded'
			state.supplierArchivedConfirmationDialogOpenStatus = false
		})
		builder.addCase(updateArchiveStatus.rejected, (state, action) => {
			state.updateArchiveApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(deleteSupplier.pending, (state, action) => {
			state.deleteSupplierApiStatus = 'loading'
		})
		builder.addCase(deleteSupplier.fulfilled, (state, action) => {
			state.deleteSupplierApiStatus = 'succeeded'
			state.supplierDeleteDialogOpenStatus = false
		})
		builder.addCase(deleteSupplier.rejected, (state, action) => {
			state.deleteSupplierApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addMatcher(
			suppliersApi.endpoints.fetchSuppliersByTenantId.matchFulfilled,
			(state, action) => {
				state.suppliersData = action.payload
			}
		)
	},
})

export const {
	setCreateSupplierModalOpen,
	setViewSupplierOpen,
	setSupplierDeleteDialogOpenStatus,
	setEditSupplierMenuAnchorEl,
	setSupplierEditModalStatus,
	setCurrentSupplier,
	setSupplierArchivedConfirmationDialogOpenStatus,
	setImportSuppliersMessageDialog,
	setImportSupplierModalOpen,
} = supplierSlice.actions

export default supplierSlice.reducer
